import { z } from 'zod';
import { CHARGE_TYPES, PAYMENT_METHODS, PAYMENT_STATUSES } from '../enums.js';
import { positiveMoneySchema, sumMoney } from '../money.js';
import { dateOnlySchema, optionalText, uuidSchema } from './common.js';
export const chargeTypeSchema = z.enum(CHARGE_TYPES);
export const paymentMethodSchema = z.enum(PAYMENT_METHODS);
export const paymentStatusSchema = z.enum(PAYMENT_STATUSES);
export const chargeListQuerySchema = z.object({
    leaseId: uuidSchema.optional(),
    chargeType: chargeTypeSchema.optional(),
    includeVoided: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
});
export const paymentListQuerySchema = z.object({
    leaseId: uuidSchema.optional(),
    tenantId: uuidSchema.optional(),
    status: paymentStatusSchema.optional(),
});
/** Charges are voided, never deleted — the obligation history stays intact. */
export const voidChargeSchema = z.object({
    reason: optionalText(500),
});
/**
 * A charge is an obligation. Its balance is *derived* from unreversed
 * allocations (invariant 3, see the `open_charges` view) — there is no
 * `balance` column to keep in sync, and adding one would be a bug.
 */
export const createChargeSchema = z.object({
    leaseId: uuidSchema,
    chargeType: chargeTypeSchema,
    amount: positiveMoneySchema,
    dueDate: dateOnlySchema,
    description: optionalText(500),
    /** Set by the monthly generation job so re-runs are idempotent. */
    periodStart: dateOnlySchema.nullish(),
    periodEnd: dateOnlySchema.nullish(),
});
/**
 * Manual payment entry (check, cash, money order). ACH payments are created by
 * the Stripe webhook path instead, never by this endpoint.
 */
export const recordManualPaymentSchema = z.object({
    leaseId: uuidSchema,
    /** Which tenant handed it over. Null for an unattributed lease-level payment. */
    tenantId: uuidSchema.nullish(),
    method: z.enum(['CHECK', 'CASH', 'MONEY_ORDER', 'OTHER']),
    amount: positiveMoneySchema,
    receivedOn: dateOnlySchema,
    reference: optionalText(120),
    notes: optionalText(2000),
});
/**
 * How a payment is applied across charges. A tenant paying $800 against a $1,250
 * rent charge plus a $50 late fee is the normal case, not an edge case — which is
 * why allocations are explicit rows rather than an implicit FIFO rule.
 */
export const allocationLineSchema = z.object({
    chargeId: uuidSchema,
    amount: positiveMoneySchema,
});
export const allocatePaymentSchema = z.object({
    paymentId: uuidSchema,
    allocations: z.array(allocationLineSchema).min(1).max(100),
});
/** Allocated total may not exceed the payment. The service also re-checks
 *  against each charge's *current* derived balance inside the transaction —
 *  this schema cannot see concurrent allocations. */
export function allocationTotal(input) {
    return sumMoney(input.allocations.map((a) => a.amount));
}
/**
 * A return (NSF). Never delete the original allocation — set `reversedAt` and
 * raise an NSF charge (invariant 3). Covers both a manual reversal (a bounced
 * check) and, once the Stripe webhook path exists, an ACH return arriving as
 * `charge.dispute.created` — up to 60 days after settlement, with no appeal
 * once filed (invariant 4). Either way this must be exact: it is the one path
 * that gets to unwind money already counted as received.
 */
export const reversePaymentSchema = z.object({
    paymentId: uuidSchema,
    returnedOn: dateOnlySchema,
    /** ACH return code, e.g. R01 insufficient funds, R10 unauthorized. */
    returnCode: optionalText(16),
    reason: optionalText(500),
    assessNsfFee: z.boolean().default(true),
});
export const rentRollQuerySchema = z.object({
    asOf: dateOnlySchema.optional(),
    propertyId: uuidSchema.optional(),
});
export const delinquencyQuerySchema = z.object({
    asOf: dateOnlySchema.optional(),
    minDaysLate: z.coerce.number().int().min(0).max(3650).default(1),
});
/** Individual open charges across the portfolio, for the dashboard pane. */
export const openChargesQuerySchema = z.object({
    propertyId: uuidSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/** Open charges grouped one-per-lease, for /staff/open-charges. Unlimited —
 *  a truncated grouping would silently drop whole leases. */
export const openChargesByLeaseQuerySchema = z.object({
    propertyId: uuidSchema.optional(),
});
//# sourceMappingURL=ledger.js.map